/**
 * Purpose: Static host reaction messages for Phase 1 (AI host plug-in point for Phase 2).
 * Owns: Reaction strings for game events, random reaction selection.
 * Notes: Phase 1 uses curated static strings. Phase 2 will replace these with
 *        AI-generated personality reactions (game show host style). The getReaction()
 *        interface stays the same — only the implementation changes.
 */

// _______________________________________________
//              REACTION EVENT TYPES
// _______________________________________________

export type ReactionEvent =
  | 'correctAnswer'
  | 'wrongAnswer'
  | 'takeLead'
  | 'streak'
  | 'comeback';

// _______________________________________________
//              STATIC REACTION BANKS
// _______________________________________________
// Each event type has an array of possible reactions.
// getReaction() picks one at random and inserts the player's name.

const CORRECT_ANSWER_REACTIONS = [
  '{name} nailed it!',
  'That\'s correct, {name}!',
  'Well done, {name}!',
  '{name} knows their stuff!',
  'Brilliant answer, {name}!',
  '{name} is on a roll!',
  'Right on the money, {name}!',
  '{name} crushed that one!',
  'Look at {name} go!',
  'Easy points for {name}!',
  'Nothing gets past {name}!',
  '{name}, that was impressive!',
];

const WRONG_ANSWER_REACTIONS = [
  'Not quite, {name} — you\'ll get the next one!',
  'Good try, {name}!',
  'So close, {name}!',
  'That\'s a tough one, {name}. No worries!',
  'Nice guess, {name}. Keep going!',
  '{name}, don\'t sweat it — next turn is yours!',
  'Tricky question! Hang in there, {name}!',
  'Almost, {name}! Stay in the game!',
  'That one stumped {name}, but the comeback is coming!',
  'No points this time, {name}, but plenty of game left!',
  'Even the best miss sometimes, {name}!',
];

const TAKE_LEAD_REACTIONS = [
  '{name} takes the lead!',
  'Watch out — {name} is in first place!',
  '{name} just moved to the top!',
  'New leader alert: {name}!',
  '{name} is running away with it!',
  'The classroom has a new star: {name}!',
];

const STREAK_REACTIONS = [
  '{name} is on fire!',
  'Unstoppable {name} keeps it going!',
  '{name} has a streak going — can anyone stop them?',
  'Hot streak for {name}!',
  '{name} is absolutely dominating right now!',
  'Nobody can cool down {name}!',
];

const COMEBACK_REACTIONS = [
  '{name} is making a comeback!',
  'Don\'t count {name} out just yet!',
  '{name} is clawing their way back!',
  'The comeback kid: {name}!',
  '{name} refuses to give up!',
  'What a turnaround by {name}!',
];

// _______________________________________________
//              REACTION LOOKUP
// _______________________________________________

const REACTION_MAP: Record<ReactionEvent, string[]> = {
  correctAnswer: CORRECT_ANSWER_REACTIONS,
  wrongAnswer: WRONG_ANSWER_REACTIONS,
  takeLead: TAKE_LEAD_REACTIONS,
  streak: STREAK_REACTIONS,
  comeback: COMEBACK_REACTIONS,
};

// _______________________________________________
//              PUBLIC API
// _______________________________________________

/**
 * Get a random reaction string for a game event.
 * Replaces {name} placeholder with the player's actual name.
 *
 * @param event - The type of game event that just occurred
 * @param playerName - The player's display name to insert into the reaction
 * @returns A human-readable reaction string ready for display
 */
export function getReaction(event: ReactionEvent, playerName: string): string {
  const reactions = REACTION_MAP[event];
  const index = Math.floor(Math.random() * reactions.length);
  const template = reactions[index];
  return template.replace(/{name}/g, playerName);
}
